import { useReturnRegister } from "@/data/Content/RegisterReturn";
import { ContentContext } from "@/data/context/content";
import { useLocalization } from "@/data/Localization";
import { Box, Button, Grid, Paper, Stack, TextField, Theme, Typography, useMediaQuery } from "@mui/material";
import { FC, useContext, useEffect, useMemo, useState } from "react";
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import Avatar from "@mui/material/Avatar";
import { Linkable } from "@/components/blocks/Linkable";
import { ContentRecommendation } from "../../ContentRecommendation";
import { EMPTY_STRING } from "../../USPsDeliveryTerms";
import { ReturnOrderFormContainerSX, displayProductDataGridSX } from "../styles/returnOrder";
import { baseColumnOptions, initialColumnWidth } from "@/data/constants/returnOrder";
import { DisplayProduct } from "@/data/types/ReturnOrder";

export const SelectReturnProduct: FC = () => {
    const returnOrderNLS = useLocalization('ReturnOrder');
    const {
        setActiveReturnStep,
        returnSteps,
        displayProducts,
        selectedItems,
        setSelectedItems
    } = useContext(ContentContext) as ReturnType<typeof useReturnRegister>;
    
    const [columnWidth, setColumnWidth] = useState<Record<string, number>>(initialColumnWidth);
    const isMobile = useMediaQuery((them: Theme) => them.breakpoints.down('sm'));
    const [errors, setErrors] = useState<Record<string,string>>({});

    useEffect(()=>{
        setSelectedItems([]);// Resetting the previously selected values on load of this content
    },[])

    useEffect(() => {
        if (isMobile) {
            const containerWidth = (document.getElementById("product_table_container") as HTMLElement).clientWidth;
            const tempColumnWidth = {
                'product_image': containerWidth * 0.15,
                'product_details': containerWidth * 0.4,
                'quantity': containerWidth * 0.27
            }
            setColumnWidth(tempColumnWidth);
        }
    }, [isMobile])

    const columns: GridColDef[] = useMemo(() => {
        return [
            {
                field: 'product_image',
                headerName: '',
                width: columnWidth['product_image'],
                renderCell: (params) => <Avatar src={params.row.imgSource} variant="square" />,
                ...baseColumnOptions,
                align: 'left',
                headerAlign: 'left',
            },
            {
                field: 'product_details',
                headerName: 'Product',
                width: columnWidth['product_details'],

                renderCell: (params) => {
                    return (
                        <Stack justifyContent='flex-start'>
                            <Linkable href={params.row.href} sx={{color:'#009AD8'}}>{params.row.partNumber}</Linkable>
                            <Box sx={{ wordBreak: 'break-all', maxWidth: 300 , fontSize:16}}><Typography >{params.row.name}</Typography></Box>
                        </Stack>
                    );
                },
                ...baseColumnOptions,
                align: 'left',
                headerAlign: 'left',
            },
            {
                field: 'quantity',
                headerName: 'Aantal',
                type: 'number',

                renderCell: (params) => {
                    const [quantity, setQuantity] = useState<string>(params.row.quantity);
                    return (
                        <Box>
                            <TextField 
                                sx={{textAlign:'center'}} 
                                size="small" 
                                value={quantity} 
                                onChange={(event) => setQuantity(event.target.value)} 
                                id={`quantity_input_${params.row.id}`}
                                className="quantity-input"
                                type="number"
                            />
                        </Box>
                        );
                },
                width: columnWidth['quantity'],
                ...baseColumnOptions,
                align: 'left',
                headerAlign: 'left',
            }
        ];
    }, [columnWidth]);

    const validateSelectedItems = () => {
        const tempErrors:Record<string,string> = {};
        const qtyElems = document.querySelectorAll('.validation-failed') as NodeListOf<Element>;
        qtyElems.forEach(qtyElem => qtyElem.classList.remove("validation-failed"));
        if(selectedItems.length===0){
            tempErrors.form_error = returnOrderNLS.Errors.ERROR_RETURN_NO_PRODUCT_SELECTION.t();
        }
        for(let i=0;i<selectedItems.length;i++){
            const si = selectedItems[i];
            const qtyElem = document.querySelector(`#quantity_input_${si.id}`) as HTMLInputElement;
            const displayProduct = displayProducts.find(dp=>dp.id===si.id) as DisplayProduct;
            if(qtyElem){
                if(qtyElem.value===EMPTY_STRING){
                    tempErrors.form_error = returnOrderNLS.Errors.ERROR_RETURN_QUANTITY_EMPTY.t();
                    qtyElem.classList.add("validation-failed");
                    break;
                }
                const inputQty = parseInt(qtyElem.value);
                if(inputQty===0){
                    tempErrors.form_error = returnOrderNLS.Errors.ERROR_RETURN_QUANTITY_ZERO.t();
                    qtyElem.classList.add("validation-failed");
                    break;
                }
                if(inputQty>displayProduct.quantity){
                    tempErrors.form_error = returnOrderNLS.Errors.ERROR_RETURN_QUANTITY_MORE.t();
                    qtyElem.classList.add("validation-failed");
                    break;
                }
                si.quantity = inputQty;
                selectedItems[i] = si;
            }
        }
        setErrors(tempErrors);
        if(Object.keys(tempErrors).length===0){
            setSelectedItems(selectedItems);
            setActiveReturnStep(returnSteps.selectReason);
        }
    }

    return (
        <Paper sx={ReturnOrderFormContainerSX}>
            <Stack>
                <Typography variant="body1AsH1" fontSize={16} mb={1}>
                    {returnOrderNLS.ProductSelect.selectProductData.t()}
                </Typography>
            </Stack>
            <Paper sx={{ display: 'flex', width: isMobile?'100%':'fit-content'}} id="product_table_container">
                <DataGrid
                    sx={displayProductDataGridSX}
                    rows={displayProducts}
                    columns={columns}
                    hideFooterPagination
                    checkboxSelection
                    getRowHeight={() => 'auto'}
                    disableRowSelectionOnClick
                    onRowSelectionModelChange={(rowSelectionModel: GridRowSelectionModel) => {
                        setSelectedItems(rowSelectionModel.map(rs=>{
                            return{
                                id:rs as string,
                                quantity:0
                            }
                        }))
                    }}
                    hideFooter
                />
            </Paper>
            {errors.form_error && 
                <Stack mt={1}>
                    <Typography color="red" variant="body1AsH2">{errors.form_error}</Typography>
                </Stack>
            }
            <Stack my={2}>
                <ContentRecommendation
                    id='IT-Returns-Product-Display-Message-eSpot'
                    properties={{
                        emsName: 'IT-Returns-Product-Display-Message-eSpot'
                    }}
                />
            </Stack>
            <Grid container rowSpacing={2} columnSpacing={2}>
                <Grid item xs={12} md={3}>
                    <Linkable
                        onClick={() => setActiveReturnStep(returnSteps.addressCheck)}
                        type="button"
                        variant="outlined"
                        id="return-order-step-back-button"
                        data-testid="return-order-step-back-button"
                        sx={{width:'100%'}}
                    >
                        {returnOrderNLS.Labels.Back.t()}
                    </Linkable>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Button
                        data-testid="return-order-product-submit"
                        id="return-order-product-submit"
                        type="submit"
                        variant="contained"
                        onClick={validateSelectedItems}
                        size="small"
                        fullWidth
                    >
                        {returnOrderNLS.Labels.SelectProductSubmit.t()}
                    </Button>
                </Grid>
            </Grid>
        </Paper>
    );
};